import React, { useEffect, useState, useRef, useCallback } from 'react';
import Comments from '../components/Comments';
import '../css.css';

// 이미지 컴포넌트 (blob 로딩)
const CafeOrderImage = ({ fishName, alt, className, onError, imageCache, loadImageAsBlob }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // 어종 이름을 파일명으로 변환하는 함수
    const getImageFilename = (fishName) => {
        if (!fishName) return null;
        return `${fishName.replace(/\s+/g, '')}.webp`;
    };

    // 이미지 즉시 로드
    useEffect(() => {
        const filename = getImageFilename(fishName);
        if (filename) {
            setImageError(false);
            setImageLoaded(false);
            setImageSrc(null);
            
            loadImageAsBlob(filename).then(blobUrl => {
                if (blobUrl) {
                    setImageSrc(blobUrl);
                    setImageLoaded(true);
                } else {
                    setImageError(true);
                    if (onError) {
                        onError();
                    }
                }
            }).catch(error => {
                console.warn(`Failed to load image ${filename}:`, error);
                setImageError(true);
                if (onError) {
                    onError();
                }
            });
        }
    }, [fishName, onError, loadImageAsBlob]);

    if (imageError) {
        return (
            <div 
                className={`${className} image-error`} 
                style={{ 
                    background: '#f8f8f8', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: '#ccc', 
                    fontSize: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    width: '50px',
                    height: '50px'
                }}
            >
                이미지 없음
            </div>
        );
    }

    if (!imageLoaded || !imageSrc) {
        return (
            <div 
                className={`${className} image-placeholder`} 
                style={{ 
                    background: '#f0f0f0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: '#999',
                    width: '50px',
                    height: '50px',
                    fontSize: '8px'
                }}
            >
                Loading...
            </div>
        );
    }

    return (
        <img 
            src={imageSrc}
            alt={alt}
            className={className}
            style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            onError={() => {
                setImageError(true);
                if (onError) {
                    onError();
                }
            }}
        />
    );
};

const CafeOrderTable = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedLake, setSelectedLake] = useState('전체');
  const [imageCache, setImageCache] = useState(new Map());
  const [loadingQueue, setLoadingQueue] = useState(new Set());
  const [loadingImages, setLoadingImages] = useState(false);
  
  // blob URL 관리를 위한 ref
  const imageCacheRef = useRef(new Map());

  // 이미지를 blob으로 캐싱하는 함수
  const loadImageAsBlob = useCallback(async (filename) => {
    if (!filename) return null;
    
    // 캐시에서 확인
    if (imageCache.has(filename)) {
      const cachedUrl = imageCache.get(filename);
      return cachedUrl;
    }

    // 이미 로딩 중인 경우 대기
    if (loadingQueue.has(filename)) {
      return new Promise((resolve) => {
        const checkCache = () => {
          if (imageCache.has(filename)) {
            resolve(imageCache.get(filename));
          } else if (!loadingQueue.has(filename)) {
            resolve(null);
          } else {
            setTimeout(checkCache, 50);
          }
        };
        checkCache();
      });
    }

    // 로딩 큐에 추가
    setLoadingQueue(prev => new Set(prev.add(filename)));

    try {
      // 이미지 URL 생성
      const imageUrl = `/data/img/fishes/fishicon/${filename}`;
      
      const response = await fetch(imageUrl, {
        cache: 'force-cache',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // blob 타입 검증
      if (!blob.type.startsWith('image/')) {
        throw new Error('Invalid image type');
      }
      
      const blobUrl = URL.createObjectURL(blob);
      
      // ref에도 저장 (정리용)
      imageCacheRef.current.set(filename, blobUrl);
      
      // 캐시 크기 제한 (최대 100개)
      if (imageCache.size >= 100) {
        const firstKey = imageCache.keys().next().value;
        const oldUrl = imageCache.get(firstKey);
        if (oldUrl && typeof oldUrl === 'string' && oldUrl.startsWith('blob:')) {
          URL.revokeObjectURL(oldUrl);
          imageCacheRef.current.delete(firstKey);
        }
        imageCache.delete(firstKey);
      }
      
      setImageCache(prev => new Map(prev.set(filename, blobUrl)));
      return blobUrl;
      
    } catch (error) {
      console.warn(`Error loading image ${filename}:`, error.message);
      setImageCache(prev => new Map(prev.set(filename, null)));
      return null;
    } finally {
      // 로딩 큐에서 제거
      setLoadingQueue(prev => {
        const newQueue = new Set(prev);
        newQueue.delete(filename);
        return newQueue;
      });
    }
  }, [imageCache, loadingQueue]);

  // 모든 이미지를 미리 로드하는 함수
  const preloadAllImages = useCallback(async (dataList) => {
    if (!dataList || dataList.length === 0) return;
    
    setLoadingImages(true);
    
    const uniqueFishNames = [...new Set(dataList.map(item => item['어종']).filter(Boolean))];
    const imagePromises = uniqueFishNames.map(fishName => {
      const filename = `${fishName.replace(/\s+/g, '')}.webp`;
      return loadImageAsBlob(filename);
    });
    
    try {
      await Promise.allSettled(imagePromises);
    } catch (error) {
      console.error('Error preloading images:', error);
    } finally {
      setLoadingImages(false);
    }
  }, [loadImageAsBlob]);

  useEffect(() => {
    // API에서 카페오더 데이터 가져오기
    fetch('/api/cafeorder')
      .then((response) => {
        if (!response.ok) throw new Error('카페오더 데이터를 불러올 수 없습니다.');
        return response.json();
      })
      .then((apiData) => {
        // API 데이터를 기존 형식에 맞게 변환
        const rows = apiData.map((row) => {
          // 실버 범위를 합쳐서 새로운 필드 생성
          const silverRange = `${row['최소실버']} - ${row['최대실버']}`;
          
          // 최소 크기 단위 변환 (1000 이상이면 kg, 아니면 g)
          const minSize = parseFloat(row['최소크기']);
          let sizeDisplay;
          if (!isNaN(minSize)) {
            if (minSize >= 1000) {
              sizeDisplay = `${(minSize / 1000)} kg`;
            } else {
              sizeDisplay = `${minSize} g`;
            }
          } else {
            sizeDisplay = row['최소크기'];
          }
          
          return {
            '수역': row['수역'],
            '어종': row['어종'],
            '최소 크기': row['최소크기'],
            '수량': row['수량'],
            '최소 실버': row['최소실버'],
            '최대 실버': row['최대실버'],
            '실버 범위': silverRange,
            '최소 크기 표시': sizeDisplay
          };
        });
        
        setData(rows);
        
        // 데이터 로드 후 이미지 프리로딩
        if (rows.length > 0) {
          preloadAllImages(rows);
        }
      })
      .catch((err) => {
        setError(err.message);
      });
  }, [preloadAllImages]);

  // 컴포넌트 언마운트 시 blob URL 정리
  useEffect(() => {
    const currentCache = imageCacheRef.current;
    return () => {
      currentCache.forEach(blobUrl => {
        if (blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      });
      currentCache.clear();
    };
  }, []);

  // 수역 목록 추출 (공백 제거 후 중복 제거)
  const lakeList = [
    '모기 호수',
    '엘크 호수',
    '굽이치는 개울',
    '올드 버그 호수',
    '벨라야 강',
    '쿠오리 호수',
    '볼호프 강',
    '세베르스키 도네츠 강',
    '수라 강',
    '라도가 호수',
    '엠버 호수',
    '라도가 군도',
    '악투바 강',
    '카퍼 호수',
    '니즈냐야 퉁구스카 강',
    '야마 강',
    '노르웨이 해',
  ];
  
  // 각 수역별 데이터 개수 계산
  const lakeCount = lakeList.reduce((acc, lake) => {
    acc[lake] = data.filter(row => row['수역']?.trim() === lake).length;
    return acc;
  }, {});
  // 전체 개수
  const totalCount = data.length;

  // 선택된 수역에 따라 필터링
  const filteredData = selectedLake === '전체' 
    ? data.slice(0, 30)  // '전체' 선택 시 30개만 표시
    : data.filter(row => row['수역'] === selectedLake);

  if (error) return <div style={{ color: 'red' }}>에러: {error}</div>;
  if (!data.length) return <div>로딩 중...</div>;

  return (
    <>
      <h1 className="page-title">카페오더</h1>
      <div className="page-content">
        {loadingImages && (
          <div style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
            이미지 로딩 중...
          </div>
        )}
        <div className="cafe-order-filter">
          <label>
            수역 선택:
            <select 
              className="cafe-order-select" 
              value={selectedLake} 
              onChange={e => setSelectedLake(e.target.value)}
            >
              <option value="전체">전체 ({totalCount})</option>
              {lakeList.map((lake, idx) => (
                <option key={idx} value={lake}>{lake} ({lakeCount[lake]})</option>
              ))}
            </select>
          </label>
        </div>
        <div className="oil-group">
          <table className="oil-table" style={{maxWidth: '900px'}}>
            <thead>
              <tr>
                <th>수역</th>
                <th>어종</th>
                <th>최소 크기</th>
                <th>수량</th>
                <th>실버 범위</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row['수역']}</td>
                  <td>
                    <div className="cafe-order-fish-cell">
                      <CafeOrderImage
                        fishName={row['어종']}
                        alt={row['어종']}
                        className="cafe-order-fish-image"
                        imageCache={imageCache}
                        loadImageAsBlob={loadImageAsBlob}
                        onError={() => {
                          console.warn('Failed to load fish image:', row['어종']);
                        }}
                      />
                      <span className="cafe-order-fish-name">{row['어종']}</span>
                    </div>
                  </td>
                  <td>{row['최소 크기 표시']}</td>
                  <td>{row['수량']}</td>
                  <td>{row['실버 범위']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 댓글 시스템 */}
      <Comments pageName="cafeorder" />

    </>
  );
};

export default CafeOrderTable;
